import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form } from 'antd';

import DownOutlined from '../../../assets/imgs/icDown.svg?react';
import Header from '../../../components/header/Header';
import CustomAntButton from '../../../components/CustomAntButton/CustomAntButton';
import CustomSelect from '../../../components/CustomSelect/CustomSelect';
import FujiModal from '../../../components/fuji-modal/FujiModal';
import { getValueByLanguage, hasSelectedNumber, showNotificationDialog } from '../../../utils';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import usePurchaseTicket from '../../../hooks/usePurchaseTicket';

import './styles.css';
import styles from './styles.module.css';
import { MultilingualType, OrderTicket, SelectNumber } from 'types';

const BoughtTicketDistributionScreen = () => {
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [number, setNumber] = useState<SelectNumber>({});
    const [parentOrderId, setParentOrderId] = useState<string>();
    const { t, i18n } = useTranslation();
    const { language } = i18n;
    const location = useLocation();
    const { orderTicket }: { orderTicket: OrderTicket } = location.state ?? {};
    const { distributionsOrderTicket, isLoading } = usePurchaseTicket();
    const l = useCallback((text?: MultilingualType | null) => getValueByLanguage(text, language), [language]);
    const navigate = useNavigate();

    const selectOptions = useMemo(() => {
        const personOptionsFormat = (minCount: number | null, maxCount: number | null) => {
            const personOptions = [];
            const minSelected = minCount ?? 0;
            const maxSelected = maxCount ?? 9;

            if (maxSelected === 0 || minSelected > maxSelected) {
                personOptions.push({
                    label: t('ticket-detail.select-number', { number: maxSelected }),
                    value: maxSelected,
                });
            } else {
                for (let number = minSelected; number <= maxSelected; number++) {
                    personOptions.push({
                        label: t('ticket-detail.select-number', { number: number }),
                        value: number,
                    });
                }
            }

            return personOptions;
        };

        const options = [];
        const { remainingNumberAdults, remainingNumberCustom1, remainingNumberCustom2, remainingNumberCustom3 } =
            orderTicket?.remainingOrderDistribution ?? {};

        if ((remainingNumberAdults ?? 0) > 0) {
            options.push({
                key: 'numberAdults',
                label: t('ticket-detail.adult'),
                defaultNumber: 0,
                personOptions: personOptionsFormat(0, remainingNumberAdults),
            });
        }
        if ((remainingNumberCustom1 ?? 0) > 0) {
            options.push({
                key: 'numberCustom1',
                label: l(orderTicket.custom1Name),
                defaultNumber: 0,
                personOptions: personOptionsFormat(0, remainingNumberCustom1),
            });
        }
        if ((remainingNumberCustom2 ?? 0) > 0) {
            options.push({
                key: 'numberCustom2',
                label: l(orderTicket.custom2Name),
                defaultNumber: 0,
                personOptions: personOptionsFormat(0, remainingNumberCustom2),
            });
        }
        if ((remainingNumberCustom3 ?? 0) > 0) {
            options.push({
                key: 'numberCustom3',
                label: l(orderTicket.custom3Name),
                defaultNumber: 0,
                personOptions: personOptionsFormat(0, remainingNumberCustom3),
            });
        }
        return options;
    }, [orderTicket, l, t]);

    const handleSubmit = useCallback((form: SelectNumber) => {
        if (!hasSelectedNumber(form)) {
            showNotificationDialog(
                'error',
                'selectNumberError',
                t('notification.title'),
                t('validate.minimum-one-person'),
            );
            return;
        }
        setNumber(form);
        setIsConfirmModalOpen(true);
    }, []);

    const confirmModalClose = useCallback(() => {
        setIsConfirmModalOpen(false);
    }, []);

    const successModalClose = useCallback(() => {
        setIsSuccessModalOpen(false);
    }, []);

    const handleDistributions = useCallback(async () => {
        setIsConfirmModalOpen(false);
        const orderId = await distributionsOrderTicket({
            orderTicketId: orderTicket?.orderTicketId,
            distributeCount: number,
        });
        if (orderId) {
            setParentOrderId(orderId);
            setIsSuccessModalOpen(true);
        }
    }, [orderTicket, number]);

    const handleDistributionsInfoClick = useCallback(() => {
        navigate('/bought/distributions/info', { state: { orderTicketId: parentOrderId } });
    }, [parentOrderId]);

    return (
        <>
            <Header title={t('header.distributions')} />
            <FujiModal
                isOpen={isConfirmModalOpen}
                onCancel={confirmModalClose}
                txtCancel={t('modal.cancel') ?? ''}
                onSubmit={() => void handleDistributions()}
                txtSubmit={t('modal.ok')}
                body={(() => {
                    const numberAdults = number?.numberAdults ?? 0;
                    const numberCustom1 = number?.numberCustom1 ?? 0;
                    const numberCustom2 = number?.numberCustom2 ?? 0;
                    const numberCustom3 = number?.numberCustom3 ?? 0;

                    return (
                        <div>
                            {numberAdults > 0 &&
                                `${t('ticket-detail.adult-label')}${t('distributions.amount', {
                                    number: number.numberAdults,
                                })}`}
                            {numberCustom1 > 0 &&
                                `${numberAdults > 0 ? t('distributions.comma') : ''}${l(orderTicket?.custom1Name)}${t(
                                    'distributions.amount',
                                    {
                                        number: number.numberCustom1,
                                    },
                                )}`}
                            {numberCustom2 > 0 &&
                                `${numberAdults > 0 || numberCustom1 > 0 ? t('distributions.comma') : ''}${l(
                                    orderTicket?.custom2Name,
                                )}${t('distributions.amount', {
                                    number: number.numberCustom2,
                                })}`}
                            {numberCustom3 > 0 &&
                                `${
                                    numberAdults > 0 || numberCustom1 > 0 || numberCustom2 > 0
                                        ? t('distributions.comma')
                                        : ''
                                }${l(orderTicket?.custom3Name)}${t('distributions.amount', {
                                    number: number.numberCustom3,
                                })}`}
                            {t('modal.distributions-confirm')}
                        </div>
                    );
                })()}
            />

            <FujiModal
                isOpen={isSuccessModalOpen}
                onCancel={successModalClose}
                txtCancel={t('button.close') ?? ''}
                onSubmit={handleDistributionsInfoClick}
                txtSubmit={t('modal.situation')}
                body={<div>{t('modal.distributions-success')}</div>}
            />
            {isLoading && <CenterLoading />}
            <div className={`content-container scrollable`}>
                {/* 人数を選択 */}
                <div className={styles.personPicker}>
                    <div className={styles.title}>{t('validate.minimum-one-person')}</div>
                    <Form onFinish={handleSubmit} autoComplete="off">
                        {selectOptions.map((item) => (
                            <div key={item.key}>
                                <p className={styles.label}>{item.label}</p>
                                <Form.Item name={item.key} initialValue={item.defaultNumber}>
                                    <CustomSelect
                                        listHeight={200}
                                        size="large"
                                        variant="plain"
                                        className={`${styles.selectPerson} select-number`}
                                        options={item.personOptions}
                                        suffixIcon={<DownOutlined className={styles.downIcon} />}
                                        getPopupContainer={(trigger: HTMLElement) => trigger.parentNode}
                                    />
                                </Form.Item>
                            </div>
                        ))}
                        <Form.Item>
                            <div className={styles.btn}>
                                <CustomAntButton type="outlined" block htmlType="submit" className={styles.submitBtn}>
                                    {t('button.distributions')}
                                </CustomAntButton>
                            </div>
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </>
    );
};

export default BoughtTicketDistributionScreen;
